import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import {
  User,
  Phone,
  MapPin,
  MessageSquare,
  Check,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  ShieldCheck,
  Truck,
  Plus,
  Minus,
  X,
  Tag
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { orderAPI, contentAPI, api } from '../services/api';
import { OrderFormData, DeliveryOption, CartItem } from '../types';
import './Checkout.css';

import { trackFacebookEvent } from '../utils/facebookPixel';
import { gtmEvents } from '../utils/gtmDataLayer';
import { getAdAttributionData } from '../utils/adTracker';

const Checkout: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { items, clearCart, updateQuantity, removeItem } = useCart();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [directOrderLoaded, setDirectOrderLoaded] = useState(false);
  const [directOrderItems, setDirectOrderItems] = useState<CartItem[]>([]);
  const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOption[]>([]);
  const [selectedDeliveryOption, setSelectedDeliveryOption] = useState<number | null>(null);

  // Accordion state for Payment Method
  const [isPaymentOpen, setIsPaymentOpen] = useState(true);

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<{
    code: string;
    discount_amount: number;
    description?: string;
  } | null>(null);

  const [formData, setFormData] = useState<OrderFormData>({
    customer_name: '',
    customer_phone: '',
    shipping_address: '',
    notes: '',
  });

  // PreOrder state for auto-save (persisted across page reloads via sessionStorage)
  const [preorderId, setPreorderId] = useState<number | null>(() => {
    try {
      const saved = sessionStorage.getItem('checkout_preorder_id');
      return saved ? parseInt(saved, 10) : null;
    } catch {
      return null;
    }
  });
  const [, setIsSavingPreorder] = useState(false);
  const preorderSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Handle direct order data from sessionStorage — uses local state, NOT CartContext
  useEffect(() => {
    const directOrderData = sessionStorage.getItem('directOrder');
    if (directOrderData && !directOrderLoaded) {
      try {
        const orderData = JSON.parse(directOrderData);

        // Use the product data directly from sessionStorage (already fetched)
        if (orderData.product) {
          const quantity = parseInt(orderData.quantity, 10) || 1;
          setDirectOrderItems([{
            product: orderData.product,
            quantity,
            unit_price: orderData.unit_price ?? orderData.product?.final_price,
            selected_color: orderData.selected_color || '',
            selected_size: orderData.selected_size || '',
            selected_weight: orderData.selected_weight || '',
            selected_attributes: orderData.selected_attributes || {},
          }]);
          sessionStorage.removeItem('directOrder');
          setDirectOrderLoaded(true);
        }
      } catch (err) {
        console.error('Error handling direct order:', err);
        setDirectOrderLoaded(true);
      }
    }
  }, [directOrderLoaded]);

  // Computed: use direct order items if present, otherwise use cart items
  const isDirectOrder = directOrderItems.length > 0;
  const activeItems = isDirectOrder ? directOrderItems : items;

  // Fetch delivery options
  useEffect(() => {
    const fetchDeliveryOptions = async () => {
      try {
        const response = await contentAPI.getDeliveryOptions();
        const data = Array.isArray(response) ? response : (response.results || []);
        setDeliveryOptions(data);
        if (data.length > 0) {
          setSelectedDeliveryOption(data[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch delivery options:', err);
        setDeliveryOptions([]);
      }
    };
    fetchDeliveryOptions();
  }, []);

  const selectedOption = Array.isArray(deliveryOptions) ? deliveryOptions.find(opt => opt.id === selectedDeliveryOption) : null;
  const shippingCost = selectedOption ? Number(selectedOption.cost) : 0;
  const activeTotal = activeItems.reduce((sum, item) => {
    const price = item.unit_price ?? item.product.final_price ?? item.product.price;
    return sum + price * item.quantity;
  }, 0);
  const couponDiscount = appliedCoupon ? appliedCoupon.discount_amount : 0;
  const finalTotal = Math.max(0, activeTotal - couponDiscount + shippingCost);

  // Track InitiateCheckout
  useEffect(() => {
    if (activeItems.length > 0) {
      trackFacebookEvent('InitiateCheckout', {
        content_ids: activeItems.map(item => item.product.id.toString()),
        content_type: 'product',
        value: activeTotal,
        currency: 'BDT',
        num_items: activeItems.reduce((acc, item) => acc + item.quantity, 0)
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItems.length]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Coupon validation handler
  const handleApplyCoupon = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!couponCode.trim()) return;

    setCouponLoading(true);
    setCouponError('');
    setCouponSuccess('');

    try {
      const response = await api.post('/coupons/validate_code/', {
        code: couponCode.trim().toUpperCase(),
        purchase_amount: activeTotal,
      });

      if (response.data && response.data.valid) {
        const discount = Number(response.data.discount_amount || 0);
        setAppliedCoupon({
          code: couponCode.trim().toUpperCase(),
          discount_amount: discount,
          description: response.data.coupon?.code || couponCode.trim().toUpperCase(),
        });
        setCouponSuccess(`Coupon ${couponCode.trim().toUpperCase()} applied successfully!`);
        setCouponError('');
      } else {
        setCouponError(response.data?.error || t('checkout.invalidCoupon', 'Invalid coupon code'));
      }
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        err.message ||
        t('checkout.invalidCoupon', 'Invalid coupon code');
      setCouponError(errorMsg);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
    setCouponSuccess('');
  };

  // Auto-save preorder with debounce (500ms after user stops typing)
  const savePreorder = useCallback(async () => {
    if (activeItems.length === 0) return;
    if (!formData.customer_name && !formData.customer_phone && !formData.shipping_address) return;

    setIsSavingPreorder(true);
    try {
      const preorderData = {
        customer_name: formData.customer_name || '',
        customer_phone: formData.customer_phone || '',
        shipping_address: formData.shipping_address || '',
        notes: formData.notes || '',
        delivery_option: selectedDeliveryOption,
        shipping_cost: shippingCost,
        coupon_code: appliedCoupon ? appliedCoupon.code : '',
        ...getAdAttributionData(),
        items: activeItems.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity,
          selected_color: item.selected_color || '',
          selected_size: item.selected_size || '',
          selected_weight: item.selected_weight || '',
          selected_attributes: item.selected_attributes || {},
        })),
      };

      if (preorderId) {
        try {
          await orderAPI.updatePreOrder(preorderId, preorderData);
        } catch (updateErr: any) {
          if (updateErr?.response?.status === 404 || updateErr?.response?.status === 410) {
            setPreorderId(null);
            try {
              sessionStorage.removeItem('checkout_preorder_id');
            } catch {}
            const newPreorder = await orderAPI.createPreOrder(preorderData);
            if (newPreorder && newPreorder.id) {
              setPreorderId(newPreorder.id);
              try {
                sessionStorage.setItem('checkout_preorder_id', String(newPreorder.id));
              } catch {}
            }
          } else {
            console.warn('Auto-save preorder update failed:', updateErr);
          }
        }
      } else {
        const newPreorder = await orderAPI.createPreOrder(preorderData);
        if (newPreorder && newPreorder.id) {
          setPreorderId(newPreorder.id);
          try {
            sessionStorage.setItem('checkout_preorder_id', String(newPreorder.id));
          } catch {}
        }
      }
    } catch (err) {
      console.error('Auto-save preorder failed:', err);
    } finally {
      setIsSavingPreorder(false);
    }
  }, [formData, activeItems, selectedDeliveryOption, shippingCost, preorderId, appliedCoupon]);

  // Debounced auto-save effect
  useEffect(() => {
    if (preorderSaveTimeoutRef.current) {
      clearTimeout(preorderSaveTimeoutRef.current);
    }
    preorderSaveTimeoutRef.current = setTimeout(() => {
      savePreorder();
    }, 500);

    return () => {
      if (preorderSaveTimeoutRef.current) {
        clearTimeout(preorderSaveTimeoutRef.current);
      }
    };
  }, [formData, items, selectedDeliveryOption, appliedCoupon, savePreorder]);

  const validateForm = (): boolean => {
    if (!formData.customer_name.trim()) {
      setError(t('checkout.errors.nameRequired', 'Name is required'));
      return false;
    }
    if (!formData.customer_phone.trim() || formData.customer_phone.length < 11) {
      setError(t('checkout.errors.phoneInvalid', 'Valid phone number is required (11 digits)'));
      return false;
    }
    if (!formData.shipping_address.trim()) {
      setError(t('checkout.errors.addressRequired', 'Address is required'));
      return false;
    }
    if (!selectedDeliveryOption) {
      setError(t('checkout.selectDelivery', 'Please select a delivery option'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    if (activeItems.length === 0) {
      setError(t('checkout.errors.emptyCart', 'Cart is empty'));
      return;
    }

    setLoading(true);

    try {
      // Track Facebook AddPaymentInfo
      await trackFacebookEvent('AddPaymentInfo', {
        content_ids: activeItems.map(item => item.product.id.toString()),
        content_type: 'product',
        value: finalTotal,
        currency: 'BDT',
        payment_type: 'Cash On Delivery',
        phone: formData.customer_phone || '',
        customer_phone: formData.customer_phone || '',
        customer_name: formData.customer_name || '',
      });

      // Track GTM/GA4 add_payment_info
      gtmEvents.addPaymentInfo(
        activeItems.map(item => ({
          id: item.product.id,
          name: item.product.name_en,
          price: item.product.final_price,
          quantity: item.quantity,
        })),
        finalTotal,
        'Cash On Delivery'
      );

      const orderData = {
        ...formData,
        delivery_option: selectedDeliveryOption,
        shipping_cost: shippingCost,
        coupon_code: appliedCoupon ? appliedCoupon.code : '',
        ...getAdAttributionData(),
        items: activeItems.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity,
          selected_color: item.selected_color || '',
          selected_size: item.selected_size || '',
          selected_weight: item.selected_weight || '',
          selected_attributes: item.selected_attributes || {},
        })),
      };

      let order;
      if (preorderId) {
        // Confirm existing preorder with latest full form data
        try {
          order = await orderAPI.confirmPreOrder(preorderId, orderData);
        } catch (confirmErr: any) {
          const errStatus = confirmErr?.response?.status;
          if (errStatus === 404 || errStatus === 410 || (errStatus && errStatus >= 500)) {
            console.warn('Preorder confirmation fallback to direct order creation:', confirmErr);
            setPreorderId(null);
            try {
              sessionStorage.removeItem('checkout_preorder_id');
            } catch {}
            order = await orderAPI.createOrder(orderData);
          } else {
            throw confirmErr;
          }
        }
      } else {
        order = await orderAPI.createOrder(orderData);
      }

      try {
        sessionStorage.removeItem('checkout_preorder_id');
        sessionStorage.removeItem('directOrder');
      } catch {}

      try {
        const trackedOrders = JSON.parse(localStorage.getItem('trackedOrders') || '[]');
        if (order && order.order_number && !trackedOrders.includes(order.order_number)) {
          const contentIds = activeItems.map(item => item.product.id.toString());
          const totalVal = parseFloat(String(order.final_amount || order.total_amount || finalTotal));
          const numItems = activeItems.reduce((acc, item) => acc + (item.quantity || 1), 0);

          await trackFacebookEvent('Purchase', {
            content_id: contentIds[0] || '',
            content_ids: contentIds,
            content_type: 'product',
            value: totalVal,
            currency: 'BDT',
            num_items: numItems,
            order_id: order.order_number,
            phone: formData.customer_phone || order.customer_phone || '',
            customer_phone: formData.customer_phone || order.customer_phone || '',
            customer_name: formData.customer_name || order.customer_name || '',
            email: (formData as any).customer_email || (order as any).customer?.email || (order as any).customer_email || '',
          });

          trackedOrders.push(order.order_number);
          localStorage.setItem('trackedOrders', JSON.stringify(trackedOrders));
        }
      } catch (trackErr) {
        console.warn('Checkout purchase tracking failed:', trackErr);
      }

      if (!isDirectOrder) {
        clearCart();
      }
      navigate(`/order-confirmation/${order.order_number}`);
    } catch (err: any) {
      console.error('Order creation failed:', err);

      let errorMessage = t('checkout.errors.orderFailed', 'Order creation failed');

      if (err.response?.data) {
        const errorData = err.response.data;
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.non_field_errors) {
          errorMessage = Array.isArray(errorData.non_field_errors)
            ? errorData.non_field_errors.join(', ')
            : errorData.non_field_errors;
        } else {
          const fieldErrors = Object.entries(errorData)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join('; ');
          if (fieldErrors) {
            errorMessage = fieldErrors;
          }
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (activeItems.length === 0) {
    return (
      <div className="checkout-page empty-state">
        <div className="container">
          <div className="empty-cart-content">
            <div className="empty-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
            </div>
            <h2>{t('cart.empty', 'Your Cart is Empty')}</h2>
            <p>{t('cart.emptyMessage', 'Your cart appears to be empty. Start shopping now!')}</p>
            <Link to="/products" className="btn btn-primary start-shopping-btn">
              {t('cart.continueShopping', 'Continue Shopping')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="container">

        {/* Top Progress Stepper (matches Reference Image 2) */}
        <div className="checkout-stepper-container">
          <div className="checkout-stepper">
            {/* Step 1: Cart */}
            <Link to="/cart" className="stepper-item completed">
              <div className="stepper-circle">
                <Check size={16} strokeWidth={2.8} />
              </div>
              <span className="stepper-label">{t('checkout.stepCart', 'Cart')}</span>
            </Link>

            <div className="stepper-line active"></div>

            {/* Step 2: Order Now */}
            <div className="stepper-item active">
              <div className="stepper-circle">
                <span>2</span>
              </div>
              <span className="stepper-label">{t('checkout.stepOrder', 'Order Now')}</span>
            </div>

            <div className="stepper-line"></div>

            {/* Step 3: Confirmation */}
            <div className="stepper-item">
              <div className="stepper-circle">
                <span>3</span>
              </div>
              <span className="stepper-label">{t('checkout.stepConfirm', 'Confirmation')}</span>
            </div>
          </div>
        </div>

        {/* 2-Column Responsive Layout */}
        <div className="checkout-layout">

          {/* Left Column: Customer Information Card */}
          <div className="checkout-form-column">
            <div className="checkout-card customer-info-card">

              {/* Card Header with circular badge '1' */}
              <div className="card-header-clean">
                <div className="step-circle-badge">1</div>
                <h2 className="card-title">
                  {t('checkout.customerInfo', 'Customer Information')}
                </h2>
              </div>

              {error && (
                <div className="error-alert">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              <form id="checkout-form" onSubmit={handleSubmit} className="customer-form-body">

                {/* 2-Column Grid on Laptop/Tablet, 1-Column on Mobile: Name & Phone */}
                <div className="form-row-2col">
                  {/* Name Field */}
                  <div className="clean-form-group">
                    <label htmlFor="customer_name" className="field-label">
                      {t('checkout.name', 'YOUR NAME')} <span className="required">*</span>
                    </label>
                    <div className="input-with-icon">
                      <User size={18} className="field-icon" />
                      <input
                        type="text"
                        id="customer_name"
                        name="customer_name"
                        value={formData.customer_name}
                        onChange={handleInputChange}
                        placeholder={t('checkout.namePlaceholder', 'Enter your full name')}
                        required
                        className="clean-input"
                        autoComplete="name"
                      />
                    </div>
                  </div>

                  {/* Phone Field */}
                  <div className="clean-form-group">
                    <label htmlFor="customer_phone" className="field-label">
                      {t('checkout.phone', 'YOUR PHONE NUMBER')} <span className="required">*</span>
                    </label>
                    <div className="input-with-icon">
                      <Phone size={18} className="field-icon" />
                      <input
                        type="tel"
                        id="customer_phone"
                        name="customer_phone"
                        value={formData.customer_phone}
                        onChange={handleInputChange}
                        placeholder="01XXXXXXXXX"
                        required
                        className="clean-input"
                        autoComplete="tel"
                        inputMode="numeric"
                      />
                    </div>
                  </div>
                </div>

                {/* Address Field */}
                <div className="clean-form-group">
                  <label htmlFor="shipping_address" className="field-label">
                    {t('checkout.address', 'YOUR ADDRESS')} <span className="required">*</span>
                  </label>
                  <div className="input-with-icon textarea-icon-wrapper">
                    <MapPin size={18} className="field-icon textarea-icon" />
                    <textarea
                      id="shipping_address"
                      name="shipping_address"
                      value={formData.shipping_address}
                      onChange={handleInputChange}
                      rows={2}
                      placeholder={t('checkout.addressPlaceholder', 'Enter your complete delivery address')}
                      required
                      className="clean-input clean-textarea compact-textarea"
                      autoComplete="street-address"
                    />
                  </div>
                </div>

                {/* Select Delivery Area (Pill/Chip Radio Buttons) */}
                <div className="clean-form-group delivery-area-group">
                  <label className="field-label">
                    {t('checkout.selectDeliveryArea', 'SELECT DELIVERY AREA')} <span className="required">*</span>
                  </label>
                  <div className="delivery-pills-row">
                    {deliveryOptions.map(option => {
                      const isSelected = selectedDeliveryOption === option.id;
                      const optionTitle = i18n.language === 'bn' ? (option.name_bn || option.name_en) : (option.name_en || option.name_bn);
                      return (
                        <button
                          key={option.id}
                          type="button"
                          className={`delivery-pill-btn ${isSelected ? 'selected' : ''}`}
                          onClick={() => setSelectedDeliveryOption(option.id)}
                        >
                          <span className="pill-radio-indicator">
                            {isSelected && <span className="pill-radio-inner-dot" />}
                          </span>
                          <span className="pill-label-text">
                            {optionTitle} (৳{Number(option.cost).toFixed(0)})
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Payment Method Accordion / Section */}
                <div className="clean-form-group payment-accordion-group">
                  <div
                    className="payment-accordion-header"
                    onClick={() => setIsPaymentOpen(prev => !prev)}
                    role="button"
                    tabIndex={0}
                  >
                    <label className="field-label cursor-pointer mb-0">
                      {t('checkout.paymentMethod', 'PAYMENT METHOD')} <span className="required">*</span>
                    </label>
                    <span className="accordion-chevron">
                      {isPaymentOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </span>
                  </div>

                  {isPaymentOpen && (
                    <div className="payment-accordion-content">
                      <div className="payment-option-card selected">
                        <div className="payment-radio-circle selected">
                          <div className="payment-radio-dot"></div>
                        </div>
                        <div className="payment-option-details">
                          <span className="payment-option-title">
                            {t('checkout.cashOnDelivery', 'Cash On Delivery')}
                          </span>
                          <p className="payment-option-desc">
                            {t('checkout.paymentDesc', 'Pay with cash upon delivery. No online payment required.')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Order Notes Field */}
                <div className="clean-form-group">
                  <label htmlFor="order_notes" className="field-label">
                    {t('checkout.orderNotes', 'ORDER NOTES')}
                  </label>
                  <div className="input-with-icon">
                    <MessageSquare size={16} className="field-icon" />
                    <input
                      type="text"
                      id="order_notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder={t('checkout.notesPlaceholder', 'Any special instructions for your order...')}
                      className="clean-input"
                    />
                  </div>
                </div>

              </form>
            </div>
          </div>

          {/* Right Column: Order Summary Card */}
          <div className="checkout-summary-column">
            <div className="checkout-card summary-card-clean sticky-summary">

              {/* Order Summary Header with red accent bar */}
              <div className="summary-header-wrap">
                <h3 className="summary-title">
                  {t('cart.orderSummary', 'Order Summary')}
                </h3>
                <div className="summary-accent-line"></div>
              </div>

              {/* Product List */}
              <div className="summary-products-clean">
                {activeItems.map((item) => {
                  const itemPrice = item.unit_price ?? item.product.final_price ?? item.product.price;
                  const itemTotal = itemPrice * item.quantity;
                  const itemImg = item.product.image || item.product.primary_image || (item.product.images && item.product.images[0]?.image);

                  // Extract attribute chips
                  const hasColor = !!item.selected_color;
                  const hasSize = !!item.selected_size;
                  const hasWeight = !!item.selected_weight;
                  const customAttrs = item.selected_attributes ? Object.entries(item.selected_attributes) : [];

                  return (
                    <div key={`${item.product.id}-${item.selected_color || ''}-${item.selected_size || ''}`} className="clean-product-item">
                      {/* Product Thumbnail with badge */}
                      <div className="clean-product-thumb">
                        <span className="thumb-qty-badge">{item.quantity}</span>
                        {itemImg ? (
                          <img src={itemImg} alt={item.product.name} />
                        ) : (
                          <div className="clean-placeholder-thumb">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                              <circle cx="8.5" cy="8.5" r="1.5" />
                              <polyline points="21 15 16 10 5 21" />
                            </svg>
                          </div>
                        )}
                      </div>

                      {/* Product Details */}
                      <div className="clean-product-details">
                        <div className="clean-product-title-row">
                          <h4 className="clean-product-name">
                            {i18n.language === 'bn' ? (item.product.name_bn || item.product.name) : (item.product.name_en || item.product.name)}
                          </h4>
                          <span className="clean-product-price">
                            ৳{itemTotal.toFixed(2)}
                          </span>
                        </div>

                        {/* Variant Chips (e.g. ⚪ Light Coffee, 2XL | 54 | 25) */}
                        <div className="clean-variant-chips">
                          {item.selected_color && (
                            <span className="variant-chip color-chip">
                              <span
                                className="color-dot"
                                style={{
                                  backgroundColor: item.selected_color.toLowerCase().includes('#')
                                    ? item.selected_color
                                    : (item.selected_color.toLowerCase() === 'white' ? '#FFFFFF' : item.selected_color)
                                }}
                              />
                              {item.selected_color}
                            </span>
                          )}

                          {item.selected_size && (
                            <span className="variant-chip size-chip">
                              {item.selected_size}
                            </span>
                          )}

                          {item.selected_weight && (
                            <span className="variant-chip weight-chip">
                              {item.selected_weight}
                            </span>
                          )}

                          {customAttrs.map(([attrKey, attrVal]) => {
                            if (['color', 'size', 'weight', 'রং', 'সাইজ', 'ওজন'].includes(attrKey.toLowerCase())) return null;
                            return (
                              <span key={attrKey} className="variant-chip custom-chip">
                                {attrKey}: {attrVal}
                              </span>
                            );
                          })}
                        </div>

                        {/* Sleek Quantity Controls */}
                        <div className="clean-qty-row">
                          <div className="clean-qty-stepper">
                            <button
                              type="button"
                              className="qty-stepper-btn"
                              onClick={() => {
                                if (item.quantity > 1) {
                                  if (isDirectOrder) {
                                    setDirectOrderItems(prev => prev.map(i =>
                                      i.product.id === item.product.id ? { ...i, quantity: i.quantity - 1 } : i
                                    ));
                                  } else {
                                    updateQuantity(item.product.id, item.quantity - 1);
                                  }
                                }
                              }}
                              disabled={item.quantity <= 1}
                            >
                              <Minus size={13} />
                            </button>
                            <span className="qty-stepper-count">{item.quantity}</span>
                            <button
                              type="button"
                              className="qty-stepper-btn"
                              onClick={() => {
                                if (item.quantity < item.product.stock) {
                                  if (isDirectOrder) {
                                    setDirectOrderItems(prev => prev.map(i =>
                                      i.product.id === item.product.id ? { ...i, quantity: i.quantity + 1 } : i
                                    ));
                                  } else {
                                    updateQuantity(item.product.id, item.quantity + 1);
                                  }
                                }
                              }}
                              disabled={item.quantity >= item.product.stock}
                            >
                              <Plus size={13} />
                            </button>
                          </div>

                          <button
                            type="button"
                            className="clean-remove-item-btn"
                            onClick={() => {
                              if (isDirectOrder) {
                                setDirectOrderItems(prev => prev.filter(i => i.product.id !== item.product.id));
                              } else {
                                removeItem(item.product.id);
                              }
                            }}
                            title="Remove item"
                          >
                            <X size={14} />
                          </button>
                        </div>

                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Coupon Code Section (matches Reference Image 2) */}
              <div className="summary-coupon-section">
                {!appliedCoupon ? (
                  <form onSubmit={handleApplyCoupon} className="coupon-form-clean">
                    <input
                      type="text"
                      className="clean-coupon-input"
                      placeholder={t('checkout.couponPlaceholder', 'COUPON CODE')}
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      disabled={couponLoading}
                    />
                    <button
                      type="submit"
                      className="clean-coupon-btn"
                      disabled={couponLoading || !couponCode.trim()}
                    >
                      {couponLoading ? (
                        <span className="small-spinner"></span>
                      ) : (
                        t('checkout.apply', 'Apply')
                      )}
                    </button>
                  </form>
                ) : (
                  <div className="applied-coupon-pill">
                    <div className="coupon-tag-info">
                      <Tag size={15} />
                      <span className="applied-coupon-code">{appliedCoupon.code}</span>
                      <span className="applied-coupon-discount">(-৳{appliedCoupon.discount_amount.toFixed(2)})</span>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveCoupon}
                      className="remove-coupon-btn"
                    >
                      {t('checkout.remove', 'Remove')}
                    </button>
                  </div>
                )}

                {couponError && <p className="coupon-msg error-msg">{couponError}</p>}
                {couponSuccess && <p className="coupon-msg success-msg">{couponSuccess}</p>}
              </div>

              {/* Price Breakdown */}
              <div className="clean-summary-breakdown">
                <div className="summary-calc-row">
                  <span className="calc-label">{t('checkout.totalPrice', 'Total Price')}</span>
                  <span className="calc-value">৳{activeTotal.toFixed(2)}</span>
                </div>

                {couponDiscount > 0 && (
                  <div className="summary-calc-row discount-row">
                    <span className="calc-label">{t('checkout.discount', 'Discount')}</span>
                    <span className="calc-value discount-value">-৳{couponDiscount.toFixed(2)}</span>
                  </div>
                )}

                <div className="summary-calc-row">
                  <span className="calc-label">{t('checkout.deliveryCharge', 'Delivery Charge')}</span>
                  <span className="calc-value">৳{shippingCost.toFixed(2)}</span>
                </div>

                <div className="clean-breakdown-divider"></div>

                <div className="summary-calc-row total-bill-row">
                  <span className="total-bill-label">{t('checkout.totalBill', 'Total Bill')}</span>
                  <span className="total-bill-value">৳{finalTotal.toFixed(2)}</span>
                </div>
              </div>

              {/* The Vibrant Confirm Order Button (Laptop, Tab & Mobile) */}
              <button
                type="submit"
                form="checkout-form"
                className="vibrant-confirm-order-btn"
                disabled={loading}
              >
                {loading ? (
                  <span className="vibrant-btn-spinner"></span>
                ) : (
                  <div className="vibrant-btn-content">
                    <span className="vibrant-btn-text">
                      {t('checkout.placeOrder', 'Confirm Order')}
                    </span>
                    <ArrowRight size={20} className="vibrant-btn-arrow" />
                  </div>
                )}
              </button>

              {/* Security & Delivery Trust Badges */}
              <div className="clean-trust-badges">
                <div className="trust-badge-item">
                  <ShieldCheck size={18} className="trust-badge-icon" />
                  <span>{t('checkout.secureCheckout', 'Secure Checkout')}</span>
                </div>
                <div className="trust-badge-item">
                  <Truck size={18} className="trust-badge-icon" />
                  <span>{t('checkout.trackedDelivery', 'Tracked Delivery')}</span>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Checkout;
